import { logger } from "@/core/logging";
import { convertBase64ToBlob, createDownscaledImageIfNeeded } from "@/core/media";
import {
  CAPTURE_COMPRESS_MAX_SIZE,
  CAPTURE_COMPRESS_QUALITY,
} from "@/features/camera/captureConstants";
import {
  retrieveFrontalFacialImage as retrieveFrontalFacialImageRequest,
  retrieveFrontalFacialImageValidation,
  uploadFrontalFacialImage,
  uploadLashBrowEyeCrop,
  uploadProfileFacialImage,
} from "@orval/ageless-imaging/ageless-imaging";
import type {
  DisplayFrontalFacialImage,
  DisplayFrontalFacialImageValidation,
  UploadFrontalFacialImageResponse,
} from "@orval/model";

export type PhotoSource = "front" | "left" | "right";

export type PhotoValidationPayload = {
  validationResult: string;
  faceCount: number;
  qualitySignals: string[];
};

type UploadFrontalFacialImageArgs = {
  assessmentId: string;
  platformId: string;
  photoDataUrl: string;
  /** When set, sent as the multipart filename (original upload name). */
  fileName?: string;
  maxDimension?: number;
  jpegQuality?: number;
  requestOptions?: { headers: HeadersInit };
};

export type UploadFrontalImageForAssessmentArgs = UploadFrontalFacialImageArgs;
export type UploadFrontalImageForAssessmentFn = (
  args: UploadFrontalImageForAssessmentArgs,
) => Promise<UploadFrontalFacialImageResponse>;

function toMultipartBodyFile(blob: Blob, fileName?: string): Blob {
  if (!fileName) {
    return blob;
  }
  return new File([blob], fileName, { type: blob.type || "image/jpeg" });
}

export type DisplayFrontalFacialImageResponse = DisplayFrontalFacialImage;
export type DisplayFrontalFacialImageValidationResponse = DisplayFrontalFacialImageValidation;

export const uploadProfileFacialImageForAssessment = async ({
  assessmentId,
  platformId,
  photoDataUrl,
  fileName,
  maxDimension = CAPTURE_COMPRESS_MAX_SIZE,
  jpegQuality = CAPTURE_COMPRESS_QUALITY,
  requestOptions,
}: UploadFrontalFacialImageArgs): Promise<UploadFrontalFacialImageResponse> => {
  try {
    const downscaledPhotoDataUrl = await createStudioUploadPhotoDataUrl({
      photoDataUrl,
      maxDimension,
      jpegQuality,
    });
    const blob = convertBase64ToBlob(downscaledPhotoDataUrl);
    return await uploadProfileFacialImage(
      assessmentId,
      platformId,
      {
        File: toMultipartBodyFile(blob, fileName),
      },
      requestOptions,
    );
  } catch (error) {
    logger.error(`studio_session_side_image_upload_failed`, undefined, error as Error);
    throw error;
  }
};

type UploadLashBrowEyeCropArgs = {
  assessmentId: string;
  platformId: string;
  frontalFacialImageId: string;
  photoDataUrl: string;
  fileName?: string;
  maxDimension?: number;
  jpegQuality?: number;
  requestOptions?: { headers: HeadersInit };
};

/**
 * Uploads a separate, loosely-cropped eye-region image for a previously uploaded frontal facial image.
 * The crop is consumed only by lash/brow validation and analysis; the full-face frontal image remains
 * the display/transformation source.
 */
export const uploadLashBrowEyeCropForAssessment = async ({
  assessmentId,
  platformId,
  frontalFacialImageId,
  photoDataUrl,
  fileName,
  maxDimension = CAPTURE_COMPRESS_MAX_SIZE,
  jpegQuality = CAPTURE_COMPRESS_QUALITY,
  requestOptions,
}: UploadLashBrowEyeCropArgs): Promise<void> => {
  try {
    const downscaledPhotoDataUrl = await createStudioUploadPhotoDataUrl({
      photoDataUrl,
      maxDimension,
      jpegQuality,
    });
    const blob = convertBase64ToBlob(downscaledPhotoDataUrl);
    await uploadLashBrowEyeCrop(
      assessmentId,
      platformId,
      frontalFacialImageId,
      {
        File: toMultipartBodyFile(blob, fileName),
      },
      requestOptions,
    );
  } catch (error) {
    logger.error(`lash_brow_eye_crop_upload_failed`, undefined, error as Error);
    throw error;
  }
};

export const uploadFrontalFacialImageForAssessment = async ({
  assessmentId,
  platformId,
  photoDataUrl,
  fileName,
  maxDimension = CAPTURE_COMPRESS_MAX_SIZE,
  jpegQuality = CAPTURE_COMPRESS_QUALITY,
  requestOptions,
}: UploadFrontalFacialImageArgs): Promise<UploadFrontalFacialImageResponse> => {
  try {
    const downscaledPhotoDataUrl = await createStudioUploadPhotoDataUrl({
      photoDataUrl,
      maxDimension,
      jpegQuality,
    });
    const blob = convertBase64ToBlob(downscaledPhotoDataUrl);
    return await uploadFrontalFacialImage(
      assessmentId,
      platformId,
      {
        File: toMultipartBodyFile(blob, fileName),
      },
      requestOptions,
    );
  } catch (error) {
    logger.error(`studio_session_frontal_image_upload_failed`, undefined, error as Error);
    throw error;
  }
};

type CreateStudioUploadPhotoDataUrlArgs = {
  photoDataUrl: string;
  maxDimension: number;
  jpegQuality: number;
};

async function createStudioUploadPhotoDataUrl({
  photoDataUrl,
  maxDimension,
  jpegQuality,
}: CreateStudioUploadPhotoDataUrlArgs): Promise<string> {
  try {
    return await createDownscaledImageIfNeeded(photoDataUrl, maxDimension, jpegQuality);
  } catch (error) {
    logger.warn("studio_session_frontend_downscale_failed");
    logger.error("studio_session_frontend_downscale_failed_error", undefined, error as Error);
    return photoDataUrl;
  }
}

export const getFrontalFacialImage = async (
  imageId: string,
): Promise<DisplayFrontalFacialImageResponse> => {
  try {
    return await retrieveFrontalFacialImageRequest(imageId);
  } catch (error) {
    logger.error("studio_session_frontal_image_retrieve_failed", undefined, error as Error);
    throw error;
  }
};

export const getFrontalFacialImageValidation = async (
  imageId: string,
): Promise<DisplayFrontalFacialImageValidationResponse> => {
  try {
    return await retrieveFrontalFacialImageValidation(imageId);
  } catch (error) {
    logger.error(
      "studio_session_frontal_image_validation_retrieve_failed",
      undefined,
      error as Error,
    );
    throw error;
  }
};
